<template>
  <div class="step3-auth-code">
    <!-- Section 0: 授权码展示 -->
    <div class="auth-code-section" v-if="authCode">
      <div class="success-box">
        <div class="success-title">
          <i class="el-icon-success success-icon"></i>
          <span class="title-text">复制授权码，提供给合作的网约平台进行配置</span>
        </div>
        <div class="auth-code-content">
          <span class="label">授权码：</span>
          <span class="code">{{ authCode }}</span>
          <el-button size="small" @click="handleCopy">复制授权码</el-button>
        </div>
      </div>
    </div>

    <!-- Section 1: 检测到已存在授权关系 -->
    <div class="auth-section" v-if="!authCode">
      <div class="section-header">
        <div class="blue-bar"></div>
        <span class="section-title">检测到已存在授权关系 <span class="subtitle">您可以直接复用以下授权或重新生成</span></span>
      </div>

      <div class="auth-list">
        <el-radio-group v-model="selectedAuth" class="auth-radio-group">
          <div v-for="item in authList" :key="item.id" class="auth-item" :class="{ 'is-selected': selectedAuth === item.id }">
            <el-radio :label="item.id" class="custom-radio">
              <span class="radio-label">{{ item.spName }}</span>
            </el-radio>

            <div class="auth-details">
              <el-row :gutter="20">
                <el-col :span="12">
                  <div class="detail-item">授权有效期：{{ formatValidPeriod(item) }}</div>
                </el-col>
                <el-col :span="12">
                  <div class="detail-item">授权划扣范围：{{ formatScope(item) }}</div>
                </el-col>
                <el-col :span="12">
                  <div class="detail-item">延长划扣天数：{{ item.extendDay }}天</div>
                </el-col>
                <el-col :span="12">
                  <div class="detail-item">资金代付周期：{{ item.payPeriodStr }}</div>
                </el-col>
              </el-row>
            </div>
          </div>
        </el-radio-group>
      </div>

      <div class="action-buttons">
        <el-button @click="handleRegenerate">重新生成新授权码</el-button>
        <el-button type="info" disabled v-if="!selectedAuth">使用选中授权</el-button>
        <el-button type="primary" v-else @click="handleUseSelected">使用选中授权</el-button>
      </div>
    </div>

    <!-- Section 2: 资金收款户主体信息 -->
    <div class="info-section">
      <div class="section-header">
        <div class="blue-bar"></div>
        <span class="section-title">资金收款户主体信息</span>
      </div>

      <div class="info-grid">
        <div class="info-item">
          <span class="label">企业主体全称：</span>
          <span class="value">{{ (companyInfo && companyInfo.corporationName) || '--' }}</span>
        </div>
        <div class="info-item">
          <span class="label">统一社会信用代码：</span>
          <span class="value">{{ (companyInfo && companyInfo.licenceNo) || '--' }}</span>
        </div>
        <div class="info-item">
          <span class="label">法人姓名：</span>
          <span class="value">{{ (companyInfo && companyInfo.legalName) || '--' }}</span>
        </div>

        <div class="info-item">
          <span class="label">法人手机号：</span>
          <span class="value">{{ (companyInfo && companyInfo.legalMobile) || '--' }}</span>
        </div>
        <div class="info-item">
          <span class="label">法人身份证号：</span>
          <span class="value">{{ (companyInfo && companyInfo.legalIdNo) || '--' }}</span>
        </div>
        <div class="info-item">
          <span class="label">联系人姓名：</span>
          <span class="value">{{ (companyInfo && companyInfo.contactName) || '--' }}</span>
        </div>

        <div class="info-item">
          <span class="label">联系人手机号：</span>
          <span class="value">{{ (companyInfo && companyInfo.contactMobile) || '--' }}</span>
        </div>
        <div class="info-item">
          <span class="label">联系地址：</span>
          <span class="value">{{ (companyInfo && companyInfo.contactAddress) || '--' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { regenerateSpAuthCode } from '@/api/domain/finance.js'
import { copyToClipboard } from '@/utils/index'
import { Message } from 'element-ui'

const props = defineProps({
  companyInfo: {
    type: Object,
    default: () => ({}),
  },
  authList: {
    type: Array,
    default: () => [],
  },
  authCode: {
    type: String,
    default: '',
  },
  authId: {
    type: [String, Number],
    default: '',
  },
})

const emit = defineEmits(['use-auth'])

const selectedAuth = ref(null)

const formatValidPeriod = (item) => {
  const effectDate = item?.effectDate
  const endDate = item?.endDate
  if (effectDate && endDate) {
    return `${effectDate} ~ ${endDate}`
  }
  if (effectDate) {
    return `${effectDate} ~ --`
  }
  if (endDate) {
    return `-- ~ ${endDate}`
  }
  return item?.validPeriod || '--'
}

const formatScope = (item) => {
  const list = item?.authRentCategoryName
  if (Array.isArray(list) && list.length) {
    return list.join('、')
  }
  return '--'
}

const handleUseSelected = () => {
  if (selectedAuth.value) {
    const auth = props.authList.find((item) => item.id === selectedAuth.value)
    emit('use-auth', auth)
  }
}

const handleCopy = () => {
  copyToClipboard(props.authCode, () => {
    Message.success('复制成功')
  })
}

const handleRegenerate = async () => {
  const { code, data } = await regenerateSpAuthCode({
    id: props.authId,
  })
  if (code === 1 && data) {
    emit('regenerate', data)
  }
}
</script>

<style scoped lang="scss">
.step3-auth-code {
  padding-top: 10px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-left: 0;

  .blue-bar {
    width: 4px;
    height: 16px;
    background: #409EFF;
    margin-right: 8px;
    border-radius: 2px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 500;
    color: #303133;

    .subtitle {
      font-size: 12px;
      color: #909399;
      font-weight: normal;
      margin-left: 8px;
    }
  }
}

.auth-code-section {
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 24px;

  .success-title {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #303133;
    font-weight: 500;
    margin-bottom: 12px;

    .success-icon {
      color: #67C23A;
      font-size: 20px;
      margin-right: 8px;
    }
  }

  .auth-code-content {
    display: flex;
    align-items: center;
    padding-left: 28px;

    .label {
      color: #909399;
      font-size: 13px;
    }

    .code {
      color: #606266;
      font-size: 13px;
      margin-right: 12px;
    }
  }
}

.auth-section {
  background: #F5F7FA;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 24px;

  .auth-list {
    background: #FFFFFF;
    border: 1px solid #E4E7ED;
    border-radius: 4px;
    margin-bottom: 16px;
    padding: 8px 0;
  }

  .auth-radio-group {
    width: 100%;
  }

  .auth-item {
    padding: 16px 20px;
    border-bottom: 1px solid #EBEEF5;
    transition: background-color 0.3s;

    &:last-child {
      border-bottom: none;
    }

    &.is-selected {
      background-color: #F0F9EB;
    }

    .custom-radio {
      margin-bottom: 12px;
      display: flex;
      align-items: center;
    }

    .radio-label {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
      margin-left: 8px;
    }

    .auth-details {
      padding-left: 24px;

      .detail-item {
        font-size: 12px;
        color: #606266;
        line-height: 22px;
      }
    }
  }

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}

.info-section {
  background: #F5F7FA;
  border-radius: 4px;
  padding: 16px;
}

.info-grid {
  display: flex;
  flex-wrap: wrap;
  margin-left: 12px;

  .info-item {
    width: 33.33%;
    margin-bottom: 16px;
    display: flex;
    align-items: flex-start;
    font-size: 13px;
    line-height: 20px;

    .label {
      color: #909399;
      white-space: nowrap;
      margin-right: 4px;
    }

    .value {
      color: #303133;
      word-break: break-all;
    }
  }
}
</style>
